// ── cohort-tab-shell: the shared read-tab wrapper for the cohort detail tabs ──
// Wave 3 (Cohorts). Every detail tab is its OWN component wrapping its OWN
// useHonestQuery -> <HonestState> read; this thin shell renders the section
// chrome (a Module card with a head) + the honest body so each tab stays small
// and consistent. The body renders ONLY when posture is null (ready); otherwise
// the honest state shows. Loading uses the rows skeleton so a tab body is never
// a blank.

import type { ReactNode } from "react";
import { Module, ModuleBody, ModuleHead } from "@vjal/ui";

import { HonestState } from "../../../components/honest-state";
import type { HonestGate } from "../../../services/coaching-error-posture";
import type { HonestPosture } from "../../../components/honest-state";

export interface CohortTabShellProps {
  title: string;
  subtitle?: string;
  posture: HonestPosture | null;
  gate?: HonestGate | null;
  reference?: string | null;
  onRetry?: () => void;
  /** Override the neutral empty heading/body for this read. */
  emptyTitle?: string;
  emptyMessage?: string;
  /** Domain glyph for the honest empty/gated body (navy tint circle). */
  icon?: ReactNode;
  /**
   * Header actions (e.g. role-gated Add/Import affordances) rendered in the
   * ModuleHead. Shown when the read is ready OR honest-empty (so the first record
   * can be added) — never on a gated/denied/loading read. The CALLER decides
   * whether to pass them (it already knows the posture + role gates).
   */
  headerActions?: ReactNode;
  /**
   * Posture-independent overlays (modals/dialogs). Always rendered so a modal can
   * open from the honest-empty state too. Modals portal out, so this is inert
   * until opened.
   */
  overlays?: ReactNode;
  /** Rendered only when posture === null (ready-with-data). */
  children: ReactNode;
}

export function CohortTabShell({
  title,
  subtitle,
  posture,
  gate,
  reference,
  onRetry,
  emptyTitle,
  emptyMessage,
  icon,
  headerActions,
  overlays,
  children
}: CohortTabShellProps) {
  // Affordances are shown on ready (null) or honest-empty only — a gated/denied/
  // loading read shows ONLY the honest state, never an action it can't perform.
  const actionsVisible = posture === null || posture === "empty";
  return (
    <Module span={12}>
      <ModuleHead title={title} subtitle={subtitle} actions={actionsVisible ? headerActions : undefined} />
      <ModuleBody className="space-y-4">
        {posture !== null ? (
          <HonestState
            posture={posture}
            gate={gate}
            reference={reference}
            onRetry={onRetry}
            icon={icon}
            loadingVariant="rows"
            title={posture === "empty" ? emptyTitle : undefined}
            message={posture === "empty" ? emptyMessage : undefined}
          />
        ) : (
          children
        )}
      </ModuleBody>
      {overlays}
    </Module>
  );
}
